import { MEIO_JULGAMENTO_SESSAO } from '@/dominio/MeioJulgamentoSessao';
import ptbrLocale from '@fullcalendar/core/locales/pt-br';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import AlterarPauta from '@mui/icons-material/Create';
import ExcluirPauta from '@mui/icons-material/DeleteForever';
import SalaSessao from '@mui/icons-material/Gavel';
import IncluirProcessos from '@mui/icons-material/ListAlt';
import ScreenSearchDesktopOutlinedIcon from '@mui/icons-material/ScreenSearchDesktopOutlined';
import {
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    Typography
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import React from 'react';
import '../css/fullcalendar/core/main.css';
import '../css/fullcalendar/daygrid/main.css';
import '../css/fullcalendar/list/main.css';
import '../css/fullcalendar/timegrid/main.css';
import { ROTA } from '../dominio/Rota';
import PopoverSecretaria from './PopoverSecretaria';
import ReactComponent from './ReactComponent';
import ToolbarCalendario from './ToolbarCalendario';
import ModalComposicaoPauta from './sessao/ModalComposicaoPauta';
import ModalConfirmacao, { TIPO_CONFIRMACAO } from './sessao/ModalConfirmacao';
import ModalProcuradorSessao from './sessao/ModalProcuradorSessao';

const TIPO_EVENTO_FERIADO = 'FERIADO';
const TIPO_EVENTO_SESSAO = 'SESSAO';
const SEPARADOR_SESSAO = '----';

class Calendario extends ReactComponent {
    timeoutUpdateSize;
    calendarRef = React.createRef();
    popoverAberto = false;
    acionouFechar = false;
    popoverAnchorEl = null;
    popoverInfo = null;
    modaisConfirmacao = new Map();
    exibirFeriados = true;

    constructor(props) {
        super(props);

        makeObservable(this, {
            calendarRef: observable,
            popoverAberto: observable,
            acionouFechar: observable,
            popoverAnchorEl: observable,
            popoverInfo: observable,
            modaisConfirmacao: observable,
            exibirFeriados: observable,
            sessoesJulgamento: computed,
            eventosSessoesJulgamento: computed,
            feriados: computed,
            eventosFeriados: computed,
            eventosCalendario: computed,
            eventosCalendarioFiltrados: computed,
            handleClosePopover: action,
            togglePopover: action,
            handleClickEvent: action,
            redirecionarParaSalaSessao: action,
            redirecionarParaPreSessao: action,
            handleClickAbrirSalaSessao: action,
            handleClickAbrirPreSessao: action,
            handleClickIncluirProcessos: action,
            handleClickAlterarPauta: action,
            callbackCancelar: action,
            handleClickRemoverPauta: action,
            handleClickExcluirPauta: action,
            removerPautaLocal: action,
            handleDateClick: action,
            toggleExibirFeriados: action
        });

        reaction(
            () => this.sessaoStore.sessoesFiltradas,
            (sessoesFiltradas) => {
                if (sessoesFiltradas) {
                    this?.calendarRef?.current?.getApi()?.changeView('listYear');
                } else {
                    this?.calendarRef?.current?.getApi()?.changeView('dayGridMonth');
                }
            }
        );
    }

    componentDidMount() {
        this.appStore.zerarRequisicoes();
        this.sessaoStore.carregarSessoesJulgamento();
    }

    get sessoesJulgamento() {
        return this.sessaoStore.sessoesJulgamento || [];
    }

    get eventosSessoesJulgamento() {
        return this.sessoesJulgamento.reduce((acumulador, sessao) => {
            acumulador.push({
                id: sessao.id,
                tipo: TIPO_EVENTO_SESSAO,
                sessao: sessao,
                start:
                    sessao.tipoJulgamento === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo &&
                    sessao.prazos.dataInicioVotacaoVirtual
                        ? moment(sessao.prazos.dataInicioVotacaoVirtual, 'DD-MM-YYYY-HH-mm-ss').toDate()
                        : moment(sessao.prazos.dataInicioSessaoPresencial, 'DD-MM-YYYY-HH-mm-ss').toDate(),
                end:
                    sessao.tipoJulgamento === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo &&
                    sessao.prazos.dataFimVotacaoVirtualExibicao
                        ? moment(sessao.prazos.dataFimVotacaoVirtualExibicao, 'DD-MM-YYYY-HH-mm-ss').toDate()
                        : moment(sessao.prazos.dataInicioSessaoPresencial, 'DD-MM-YYYY-HH-mm-ss').toDate(),
                color: this.getCorEvento(sessao)
            });
            return acumulador;
        }, []);
    }

    get feriados() {
        return this.dominioStore.feriados || [];
    }

    get eventosFeriados() {
        return this.feriados.reduce((acumulador, feriado) => {
            acumulador.push({
                id: feriado.id + ' - ' + feriado.data,
                tipo: TIPO_EVENTO_FERIADO,
                feriado: feriado,
                start: moment(feriado.data, 'DD-MM-YYYY').toDate(),
                end: moment(feriado.data, 'DD-MM-YYYY').add(1, 'days').toDate(),
                color: 'DodgerBlue',
                allDay: true
            });
            return acumulador;
        }, []);
    }

    get eventosCalendario() {
        let eventos = this.eventosSessoesJulgamento;

        if (this.exibirFeriados) {
            eventos = [...this.eventosSessoesJulgamento, ...this.eventosFeriados];
        }
        return eventos;
    }

    getDescricaoPauta(idPauta) {
        return this.getDescricaoSessao(idPauta);
    }

    get eventosCalendarioFiltrados() {
        if (this.sessaoStore.sessoesFiltradas) {
            return this.eventosCalendario.filter((evento) => {
                return (
                    evento.tipo === TIPO_EVENTO_SESSAO &&
                    evento?.sessao?.id?.split(SEPARADOR_SESSAO).some((sessaoId) => {
                        return this.sessaoStore.sessoesFiltradas.includes(sessaoId);
                    })
                );
            });
        }

        if (this.sessaoStore.tipoSessaoFiltrada && this.sessaoStore.tipoSessaoFiltrada !== 'A') {
            return this.eventosCalendario.filter((evento) => {
                return (
                    (evento.tipo === TIPO_EVENTO_SESSAO &&
                        this.sessaoStore.tipoSessaoFiltrada === evento.sessao.tipo) ||
                    evento.tipo === TIPO_EVENTO_FERIADO
                );
            });
        }
        return this.eventosCalendario;
    }

    getCorEvento(sessao) {
        if (
            !sessao.prazos.dataPublicacaoPauta &&
            !sessao.prazos.dataInicioVotacaoVirtual &&
            !sessao.prazos.dataFimVotacaoVirtual
        ) {
            return 'orange';
        }
        if (
            !sessao.prazos.dataPublicacaoPauta ||
            moment(sessao.prazos.dataPublicacaoPauta, 'DD-MM-YYYY-HH-mm-ss')
                .startOf('day')
                .isAfter(moment().endOf('day'), 'day')
        ) {
            return 'blue';
        }
        return 'green';
    }

    getQtdSessoesDia(dataSessao) {
        return this.sessoesJulgamento.filter((sessao) =>
            moment(sessao.prazos.dataInicioSessaoPresencial, 'DD-MM-YYYY-HH-mm-ss')
                .startOf('day')
                .isSame(moment(dataSessao, 'DD-MM-YYYY-HH-mm-ss').startOf('day'))
        ).length;
    }

    getTituloSessaoResumido(evento) {
        if (evento.tipo === TIPO_EVENTO_SESSAO) {
            const sessao = evento.sessao;
            return `${sessao.tipo} - ${sessao.tipoJulgamento.substring(0, 1)} - ${sessao.descricao}`;
        } else if (evento.tipo === TIPO_EVENTO_FERIADO) {
            const feriado = evento.feriado;
            return feriado.descricao;
        }
    }

    getTituloSessaoCompleto(evento) {
        if (evento.tipo === TIPO_EVENTO_SESSAO) {
            const sessao = evento.sessao;

            return (
                sessao.tipo +
                ' - ' +
                sessao.tipoJulgamento +
                ' - ' +
                this.getDescricoesSessoes(sessao) +
                ': ' +
                (Number.isInteger(sessao?.quantidadeTotalProcessos) ? sessao.quantidadeTotalProcessos : 0) +
                (sessao.quantidadeTotalProcessos === 1 ? ' processo' : ' processos') +
                (sessao.proxima ? ' [Próxima]' : '') +
                (sessao.hoje ? ' [Hoje]' : '')
            );
        } else if (evento.tipo === TIPO_EVENTO_FERIADO) {
            const feriado = evento.feriado;
            return feriado.descricao;
        }
    }

    getDescricoesSessoes(sessao) {
        const sessoes = Object.keys(sessao.descricaoPorSessao);

        if (sessoes.length > 1) {
            return sessoes
                .map(
                    (idPauta) =>
                        sessao.descricaoPorSessao[idPauta] + ' (' + this.getQtdProcessosPauta(sessao, idPauta) + ')'
                )
                .join('; ');
        }
        return sessao.descricao;
    }

    handleClosePopover = () => {
        this.acionouFechar = true;
        this.popoverAberto = false;
        this.popoverAnchorEl = null;
        setTimeout(() => runInAction(() => (this.acionouFechar = false)), 10);
    };

    togglePopover = (info) => {
        if (
            this.popoverAberto &&
            (!this.popoverInfo || info.event.extendedProps.sessao.id === this.popoverInfo.event.extendedProps.sessao.id)
        ) {
            this.handleClosePopover();
        } else if (!this.acionouFechar) {
            this.popoverAberto = true;
            this.popoverInfo = info;
            this.popoverAnchorEl = info.el;
            info.jsEvent.stopPropagation();
        }
    };

    handleClickEvent = (info) => {
        if (info.event.extendedProps.tipo === TIPO_EVENTO_SESSAO) {
            this.togglePopover(info);
        }
    };

    redirecionarParaSalaSessao = (sessaoId) => {
        this.sessaoStore.idSessaoSelecionada = sessaoId;
        this.redirecionar(ROTA.SESSAO.rota);
    };

    redirecionarParaPreSessao = (sessaoId) => {
        this.sessaoStore.idSessaoSelecionada = sessaoId;
        this.redirecionar(ROTA.PRE_SESSAO.rota);
    };

    handleClickAbrirSalaSessao = (sessaoId) => {
        this.redirecionarParaSalaSessao(sessaoId);
    };

    handleClickAbrirPreSessao = (sessaoId) => {
        this.redirecionarParaPreSessao(sessaoId);
    };

    handleClickIncluirProcessos = async (idSessao, idPauta) => {
        this.sessaoStore.idSessaoSelecionada = idSessao;
        this.pautaJulgamentoStore.idPautaSelecionada = idPauta;
        this.redirecionar(ROTA.PAUTA.rota);
    };

    handleClickAlterarPauta = async (sessaoId) => {
        this.handleClosePopover();
        runInAction(() => {
            this.sessaoStore.pautaJulgamentoModal = {};
            this.sessaoStore.carregandoPautaModal = true;
        });
        try {
            const sessao = await this.sessaoService.recuperarPautaJulgamento(sessaoId);
            runInAction(() => {
                this.sessaoStore.adicionarObservable(sessao.prazos, 'dataInicioSessaoPresencial');
                this.sessaoStore.adicionarObservable(sessao.prazos, 'dataInicioVotacaoVirtual');
                this.sessaoStore.adicionarObservable(sessao.prazos, 'dataFimVotacaoVirtual');
                this.sessaoStore.adicionarObservable(sessao.prazos, 'dataFimVotacaoVirtualExibicao');
                this.sessaoStore.adicionarObservable(sessao.prazos, 'dataPublicacaoPauta');
                this.sessaoStore.adicionarObservable(sessao.prazos, 'dataDisponibilizacaoPauta');
                this.sessaoStore.pautaJulgamentoModal = sessao;
                this.sessaoStore.carregandoPautaModal = false;
            });
        } catch (error) {
            runInAction(() => {
                this.sessaoStore.pautaJulgamentoModal = undefined;
                this.sessaoStore.carregandoPautaModal = false;
            });
        }
    };

    callbackCancelar = (idPauta) => {
        this.modaisConfirmacao.set(idPauta, false);
    };

    handleClickRemoverPauta = (idPauta) => {
        this.modaisConfirmacao.set(idPauta, true);
    };

    handleClickExcluirPauta = async (idPauta) => {
        const sessao = this.popoverInfo?.event?.extendedProps?.sessao;
        const qtdProcessos = sessao?.quantidadesTotaisProcessos?.[idPauta];

        if (Number.isInteger(qtdProcessos) && qtdProcessos > 0) {
            this.mensagemStore.abrirMensagemAlerta('Não é possível excluir uma pauta com processos!');
            this.modaisConfirmacao.set(idPauta, false);
        } else {
            this.modaisConfirmacao.set(idPauta, false);
            await this.sessaoService.excluirPautasJulgamento(idPauta);
            this.handleClosePopover();
            this.removerPautaLocal(sessao, idPauta);
            this.mensagemStore.abrirMensagemSucesso('Pauta excluída com sucesso');
        }
    };

    removerPautaLocal = (sessao, idPauta) => {
        const sessoesJulgamento = this.sessaoStore.sessoesJulgamento;
        const index = sessoesJulgamento.findIndex((s) => s.id === sessao.id);

        if (index === -1) return;

        const pautas = sessao.id.split(SEPARADOR_SESSAO).filter((id) => id !== idPauta);

        if (pautas.length === 0) {
            sessoesJulgamento.splice(index, 1);
        } else {
            sessoesJulgamento[index] = {
                ...sessoesJulgamento[index],
                id: pautas.join(SEPARADOR_SESSAO)
            };

            if (sessoesJulgamento[index].quantidadesTotaisProcessos) {
                delete sessoesJulgamento[index].quantidadesTotaisProcessos[idPauta];
            }
        }
    };

    handleDateClick = (info) => {
        if (info.jsEvent.detail === 2) {
            this.sessaoStore.pautaJulgamentoModal = this.sessaoStore.novaPautaJulgamento(
                this.formatarData(info, 'DD-MM-YYYY')
            );
        }
    };

    formatarData(info, formato) {
        return moment(info.date).format(formato);
    }

    handleEventDidMount = (info) => {
        if (info.view.type !== 'listYear' && info.view.type !== 'listDay') {
            info.el.style.backgroundColor = info.backgroundColor;
        }
    };

    handleDayCellDidMount = (info) => {
        if (!info.isDisabled) {
            info.el.style.cursor = 'pointer';
            this.atualizarTamanhoCalendario(info);
        }
    };

    // Hack para corrigir um problema do componente quando há barra de rolagem no calendário
    atualizarTamanhoCalendario(info) {
        if (this.timeoutUpdateSize) {
            clearTimeout(this.timeoutUpdateSize);
        }
        this.timeoutUpdateSize = setTimeout(() => info.view.calendar.updateSize(), 10);
    }

    getQtdProcessosPauta(sessao, idPauta) {
        return sessao?.quantidadesTotaisProcessos ? sessao.quantidadesTotaisProcessos[idPauta] : 0;
    }

    getTooltipPauta(sessao, idPauta) {
        return (
            sessao.descricaoPorSessao[idPauta] +
            ': ' +
            this.getQtdProcessosPauta(sessao, idPauta) +
            ' processo(s) EM PAUTA/SESSÃO'
        );
    }

    getTooltipDayCell(info) {
        return `Incluir uma sessão no dia ${this.formatarData(info, 'DD/MM/YYYY')} clicando duas vezes em qualquer local da célula`;
    }

    getArraySessoes(sessaoId) {
        return observable.array([
            {
                id: sessaoId,
                descricao: this.getDescricaoSessao(sessaoId),
                procurador: undefined,
                origem: sessaoId.split('--')[0]
            }
        ]);
    }

    getDescricaoSessao(sessaoId) {
        // @ts-ignore
        return this.popoverInfo?.event?.extendedProps?.sessao?.descricaoPorSessao[sessaoId];
    }

    getTooltipEvento(info) {
        const tipo = info.event?.extendedProps?.tipo;
        let dataPublicacao;

        if (tipo === TIPO_EVENTO_SESSAO) {
            const sessao = info.event.extendedProps.sessao;
            const prazos = sessao.prazos;
            const dataNaoPublicada = 'Pauta não publicada';
            const inicioVotacaoVirtual = moment(prazos.dataInicioVotacaoVirtual, 'DD-MM-YYYY-HH-mm-ss').format(
                'DD/MM/YYYY HH:mm'
            );
            const fimVotacaoVirtual = moment(prazos.dataFimVotacaoVirtualExibicao, 'DD-MM-YYYY-HH-mm-ss').format(
                'DD/MM/YYYY HH:mm'
            );

            if (prazos.dataPublicacaoPauta) {
                dataPublicacao = moment(prazos.dataPublicacaoPauta, 'DD-MM-YYYY-HH-mm-ss').format('DD/MM/YYYY HH:mm');
            }

            const dataLimiteEnvio = moment(prazos.dataLimiteEnvio, 'DD-MM-YYYY-HH-mm-ss').format('DD/MM/YYYY HH:mm');

            return (
                <div>
                    <Typography>{this.getTituloSessaoCompleto(sessao)}</Typography>
                    <div style={{ display: 'flex' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {prazos.dataInicioVotacaoVirtual && <Typography>Início do julgamento virtual:</Typography>}
                            {prazos.dataFimVotacaoVirtualExibicao && (
                                <Typography>Fim do julgamento virtual:</Typography>
                            )}
                            {prazos.dataLimiteEnvio && <Typography>Limite para envio ao DJEN:</Typography>}
                            <Typography>Data de publicação da pauta:</Typography>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '40px' }}>
                            {prazos.dataInicioVotacaoVirtual && <Typography>{inicioVotacaoVirtual}</Typography>}
                            {prazos.dataFimVotacaoVirtualExibicao && <Typography>{fimVotacaoVirtual}</Typography>}
                            {prazos.dataLimiteEnvio && <Typography>{dataLimiteEnvio}</Typography>}
                            <Typography>{prazos.dataPublicacaoPauta ? dataPublicacao : dataNaoPublicada}</Typography>
                        </div>
                    </div>
                    {sessao.tipoJulgamento === MEIO_JULGAMENTO_SESSAO.VIRTUAL.codigo &&
                        (!prazos.dataInicioVotacaoVirtual || !prazos.dataFimVotacaoVirtual) && (
                            <div>
                                <Typography>ALERTA: As datas da votação virtual não foram informadas</Typography>
                            </div>
                        )}
                </div>
            );
        } else if (tipo === TIPO_EVENTO_FERIADO) {
            const feriado = info.event.extendedProps.feriado;

            return (
                <List>
                    <ListItemText primary={feriado.descricao} />
                    <Divider style={{ backgroundColor: 'white' }} />
                    {feriado.tipo === 'PUBLICACAO' && <ListItem sx={{ padding: 0.5 }}>Suspende publicações</ListItem>}
                    {feriado.tipo === 'VOTACAO' && <ListItem sx={{ padding: 0.5 }}>Suspende votações</ListItem>}
                    {feriado.tipo === 'TODOS' && (
                        <ListItem sx={{ padding: 0.5 }}>Suspende publicações e votações</ListItem>
                    )}
                </List>
            );
        }
    }

    exibeEventoResumido(info) {
        return info.view.type === 'dayGridMonth' && this.getQtdSessoesDia(info.event.start) > 2;
    }

    toggleExibirFeriados = () => {
        this.exibirFeriados = !this.exibirFeriados;
    };

    render() {
        return this.sessaoStore.carregandoSessoes ? (
            <div style={{ display: 'flex' }}>
                <CircularProgress
                    style={{ margin: '20px' }}
                    disableShrink={true}
                />
                <Typography
                    color={grey[500]}
                    variant="h6"
                    sx={{ margin: '25px 10px', fontStyle: 'italic' }}
                >
                    Carregando sessões de julgamento
                </Typography>
            </div>
        ) : (
            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: '1' }}>
                <ToolbarCalendario />
                <div style={{ margin: '20px', height: '100%', display: 'inline-block' }}>
                    <FullCalendar
                        ref={this.calendarRef}
                        plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
                        locale={ptbrLocale}
                        initialView="dayGridMonth"
                        height="100%"
                        headerToolbar={{
                            left: 'buttonFeriado today prev,next',
                            center: 'title',
                            right: 'listDay,dayGridWeek,dayGridMonth,listYear'
                        }}
                        views={{
                            dayGridMonth: {
                                showNonCurrentDates: false
                            },
                            listDay: {
                                buttonText: 'Dia',
                                noEventsContent: 'Não há sessões no dia'
                            },
                            list: {
                                buttonText: 'Ano',
                                noEventsContent: 'Não há sessões a serem exibidas'
                            }
                        }}
                        customButtons={{
                            buttonFeriado: {
                                text: this.exibirFeriados ? 'Ocultar Feriados' : 'Exibir Feriados',
                                click: this.toggleExibirFeriados
                            }
                        }}
                        eventTimeFormat={{ hour: '2-digit', minute: '2-digit', omitZeroMinute: false, meridiem: false }}
                        events={this.eventosCalendarioFiltrados}
                        eventClick={this.handleClickEvent}
                        eventContent={this.renderEvento}
                        eventDidMount={this.handleEventDidMount}
                        dayCellContent={this.renderDayCell}
                        dayCellDidMount={this.handleDayCellDidMount}
                        dateClick={this.handleDateClick}
                    />
                </div>
                {this.popoverAberto && (
                    <PopoverSecretaria
                        open={this.popoverAberto}
                        anchorEl={this.popoverAnchorEl}
                        onClose={this.handleClosePopover}
                        placement="top-start"
                    >
                        {this.renderPautas()}
                    </PopoverSecretaria>
                )}
            </div>
        );
    }

    renderPautas = () => {
        if (this.popoverInfo) {
            const sessao = this.popoverInfo.event?.extendedProps?.sessao;
            const sessaoId = sessao ? sessao.id : '';
            const pautas = sessaoId ? sessaoId.split(SEPARADOR_SESSAO) : null;

            return (
                <div style={{ display: 'flex' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {pautas.map((idPauta) => (
                            <div
                                key={this.nextKey}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                            >
                                <Tooltip
                                    title={this.getTooltipPauta(sessao, idPauta)}
                                    placement="top"
                                    slotProps={{
                                        popper: { style: { zIndex: '99999' } }
                                    }}
                                >
                                    <div style={{ margin: '8px', color: '#01579B' }}>
                                        <Typography display="inline">Pauta: </Typography>
                                        {this.getDescricaoSessao(idPauta) +
                                            ' (' +
                                            this.getQtdProcessosPauta(sessao, idPauta) +
                                            ')'}
                                    </div>
                                </Tooltip>
                                <div style={{ display: 'flex' }}>
                                    <Tooltip
                                        title="Editar informações"
                                        placement="top"
                                        slotProps={{
                                            popper: { style: { zIndex: '99999' } }
                                        }}
                                    >
                                        <IconButton
                                            onClick={() => this.handleClickAlterarPauta(idPauta)}
                                            size="large"
                                        >
                                            <AlterarPauta style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </Tooltip>
                                    {this.isComponenteHabilitado('EditarComposicaoPadrao') && (
                                        <ModalComposicaoPauta sessaoId={idPauta} />
                                    )}
                                    <ModalProcuradorSessao
                                        iconButton
                                        sessoes={this.getArraySessoes(idPauta)}
                                    />
                                    {this.isComponenteHabilitado('AdicionarProcessosPauta') && (
                                        <Tooltip
                                            title="Processos da pauta/sessão"
                                            placement="top"
                                            slotProps={{
                                                popper: { style: { zIndex: '99999' } }
                                            }}
                                        >
                                            <IconButton
                                                onClick={this.handleClickIncluirProcessos.bind(this, sessaoId, idPauta)}
                                                size="large"
                                            >
                                                <IncluirProcessos />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip
                                        title="Excluir pauta"
                                        placement="top"
                                        slotProps={{
                                            popper: { style: { zIndex: '99999' } }
                                        }}
                                    >
                                        <IconButton
                                            onClick={() => this.handleClickRemoverPauta(idPauta)}
                                            size="large"
                                        >
                                            <ExcluirPauta style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </Tooltip>
                                    <ModalConfirmacao
                                        key={idPauta}
                                        open={!!this.modaisConfirmacao.get(idPauta)}
                                        titulo="Exclusão de pauta"
                                        tipoConfirmacao={TIPO_CONFIRMACAO.EXCLUSAO}
                                        callbackCancelar={() => this.callbackCancelar(idPauta)}
                                        callbackConfirmar={() => this.handleClickExcluirPauta(idPauta)}
                                    >
                                        <Typography display={'inline'}>Deseja realmente excluir a pauta </Typography>
                                        <Typography
                                            display={'inline'}
                                            fontWeight={'bold'}
                                            noWrap
                                        >
                                            {this.getDescricaoPauta(idPauta)}
                                        </Typography>
                                        <Typography display={'inline'}>?</Typography>
                                    </ModalConfirmacao>
                                </div>
                            </div>
                        ))}
                    </div>
                    {this.isComponenteHabilitado('ModuloPreSessao') && (
                        <Tooltip
                            title="Abrir pré-sessão"
                            placement="top"
                            slotProps={{
                                popper: { style: { zIndex: '99999' } }
                            }}
                        >
                            <IconButton
                                onClick={() => this.handleClickAbrirPreSessao(sessaoId)}
                                size="large"
                            >
                                <ScreenSearchDesktopOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip
                        title="Abrir sala de sessão"
                        placement="top"
                        slotProps={{
                            popper: { style: { zIndex: '99999' } }
                        }}
                    >
                        <IconButton
                            onClick={() => this.handleClickAbrirSalaSessao(sessaoId)}
                            size="large"
                        >
                            <SalaSessao />
                        </IconButton>
                    </Tooltip>
                </div>
            );
        }
        return null;
    };

    renderEvento = (info) => {
        const eventoResumido = this.exibeEventoResumido(info);
        return (
            <Tooltip
                title={this.getTooltipEvento(info)}
                placement="bottom"
            >
                <div
                    style={{
                        cursor: 'pointer',
                        margin: '0px 6px',
                        textOverflow: 'ellipsis',
                        whiteSpace: eventoResumido ? 'nowrap' : 'break-spaces',
                        overflow: 'hidden'
                    }}
                >
                    <Typography style={{ fontWeight: 700, marginRight: '6px', fontSize: '14px' }}>
                        {info.timeText}
                    </Typography>
                    <Typography fontSize="14px">
                        {eventoResumido
                            ? this.getTituloSessaoResumido(info.event.extendedProps)
                            : this.getTituloSessaoCompleto(info.event.extendedProps)}
                    </Typography>
                </div>
            </Tooltip>
        );
    };

    renderDayCell = (info) => {
        const isWeekend = info.dow === 0 || info.dow === 6;

        return (
            <Tooltip
                title={this.getTooltipDayCell(info)}
                placement="top-start"
            >
                <div style={{ cursor: 'pointer', color: isWeekend ? 'red' : 'black' }}>{info.dayNumberText}</div>
            </Tooltip>
        );
    };
}

export default inject('stores')(observer(Calendario));
